export type NavContext = {
  admin: boolean;
  showOpsNav: boolean;
  magazineAccess: boolean;
  companiesAccess: boolean;
  contactsAccess: boolean;
  magazineReports: boolean;
  salesReports: boolean;
  menuAccess?: (key: import('../../util/menuAccess').MenuAccessKey) => boolean;
};

export type NavIcon =
  | "statistics"
  | "dashboard"
  | "event"
  | "map"
  | "business"
  | "contacts"
  | "exhibitors"
  | "products"
  | "orders"
  | "publications"
  | "issues"
  | "inventory"
  | "magazineBookings"
  | "allBookings"
  | "contractTemplates"
  | "contracts"
  | "invoices"
  | "reports"
  | "profile"
  | "colleagues"
  | "permissions"
  | "system"
  | "settings"
  | "logs";

type VisibleFn = (ctx: NavContext) => boolean;

export type NavSection = {
  type: "section";
  title: string;
  visible?: VisibleFn;
};

export type NavLink = {
  type: "item";
  to: string;
  label: string;
  icon: NavIcon;
  end?: boolean;
  /** Paths that should not activate this item (e.g. sibling routes under the same prefix). */
  activeExclude?: string[];
  visible?: VisibleFn;
  accessKey?: import('../../util/menuAccess').MenuAccessKey;
};

export type NavStatic = {
  type: "static";
  label: string;
  icon: NavIcon;
  visible?: VisibleFn;
};

export type NavEntry = NavSection | NavLink | NavStatic;

const ops: VisibleFn = (c) => c.showOpsNav;
const admin: VisibleFn = (c) => c.admin;
const magazine: VisibleFn = (c) => c.magazineAccess;
const companies: VisibleFn = (c) => c.companiesAccess;
const contacts: VisibleFn = (c) => c.contactsAccess;
const magazineReports: VisibleFn = (c) => c.magazineReports;
const salesReports: VisibleFn = (c) => c.salesReports;
const always: VisibleFn = () => true;

export const NAV_CONFIG: NavEntry[] = [
  { type: "section", title: "Overview" },
  {
    type: "item",
    to: "/dashboard",
    label: "Dashboard",
    icon: "dashboard",
    visible: always,
  },
  {
    type: "item",
    to: "/reports",
    label: "Reports",
    icon: "reports",
    end: true,
    activeExclude: ["/magazine/reports"],
    visible: salesReports,
  },

  { type: "section", title: "CRM", visible: (c) => c.showOpsNav || c.companiesAccess },
  {
    type: "item",
    to: "/companys",
    label: "Companies",
    icon: "business",
    visible: companies,
  },
  {
    type: "item",
    to: "/crm/contacts",
    label: "Contacts",
    icon: "contacts",
    visible: contacts,
  },

  { type: "section", title: "Event management", visible: ops },
  { type: "item", to: "/events", label: "Events", icon: "event", visible: ops, accessKey: "events.events" },
  { type: "item", to: "/maps", label: "Maps", icon: "map", visible: ops, accessKey: "events.maps" },
  {
    type: "item",
    to: "/applicants",
    label: "Exhibitors",
    icon: "exhibitors",
    visible: ops,
    accessKey: "events.exhibitors",
  },
  {
    type: "item",
    to: "/products",
    label: "Products",
    icon: "products",
    visible: ops,
    accessKey: "events.products",
  },
  { type: "item", to: "/orders", label: "Orders", icon: "orders", visible: ops, accessKey: "events.orders" },

  { type: "section", title: "Magazine", visible: magazine },
  {
    type: "item",
    to: "/magazine/publications",
    label: "Publications",
    icon: "publications",
    visible: magazine,
    accessKey: "magazine.publications",
  },
  {
    type: "item",
    to: "/magazine/issues",
    label: "Issues",
    icon: "issues",
    visible: magazine,
    accessKey: "magazine.issues",
  },
  {
    type: "item",
    to: "/magazine/inventory",
    label: "Inventory",
    icon: "inventory",
    visible: magazine,
    accessKey: "magazine.inventory",
  },
  {
    type: "item",
    to: "/magazine/proposals",
    label: "Proposals",
    icon: "magazineBookings",
    visible: magazine,
    accessKey: "magazine.proposals",
  },
  {
    type: "item",
    to: "/magazine/reports",
    label: "Issue report",
    icon: "reports",
    visible: magazineReports,
    accessKey: "magazine.reports",
  },

  { type: "section", title: "Bookings", visible: admin },
  {
    type: "item",
    to: "/bookings",
    label: "All bookings",
    icon: "allBookings",
    visible: admin,
  },

  { type: "section", title: "Contracts", visible: magazine },
  {
    type: "item",
    to: "/contracts/templates",
    label: "Templates",
    icon: "contractTemplates",
    visible: admin,
  },
  {
    type: "item",
    to: "/contracts",
    label: "Contracts",
    icon: "contracts",
    activeExclude: ["/contracts/templates"],
    visible: magazine,
    accessKey: "magazine.contracts",
  },

  { type: "section", title: "Invoices", visible: magazine },
  {
    type: "item",
    to: "/invoices",
    label: "Invoices",
    icon: "invoices",
    visible: magazine,
    accessKey: "magazine.invoices",
  },
  {
    type: "item",
    to: "/quickbooks",
    label: "QuickBooks",
    icon: "settings",
    visible: admin,
  },

  { type: "section", title: "Team & access", visible: always },
  {
    type: "item",
    to: "/users/profile",
    label: "Profile",
    icon: "profile",
    visible: always,
  },
  {
    type: "item",
    to: "/colleagues",
    label: "Colleagues",
    icon: "colleagues",
    visible: admin,
  },
  {
    type: "item",
    to: "/permissions",
    label: "Permissions",
    icon: "permissions",
    visible: admin,
  },

  { type: "section", title: "Maintenance", visible: admin },
  {
    type: "item",
    to: "/maintenance/system",
    label: "System",
    icon: "system",
    visible: admin,
  },
  { type: "static", label: "Settings", icon: "settings", visible: admin },
  { type: "static", label: "Logs", icon: "logs", visible: admin },
];

/** Drop sections with no visible children; drop invisible entries. */
export const getVisibleNav = (ctx: NavContext): NavEntry[] => {
  const isVisible = (entry: NavEntry) =>
    (!entry.visible || entry.visible(ctx)) &&
    (entry.type !== 'item' || !entry.accessKey || !ctx.menuAccess || ctx.menuAccess(entry.accessKey));

  const result: NavEntry[] = [];
  let pendingSection: NavSection | null = null;
  let pendingItems: NavEntry[] = [];

  const flushSection = () => {
    if (pendingSection && pendingItems.length > 0) {
      result.push(pendingSection, ...pendingItems);
    }
    pendingSection = null;
    pendingItems = [];
  };

  for (const entry of NAV_CONFIG) {
    if (entry.type === "section") {
      flushSection();
      if (!isVisible(entry)) {
        pendingSection = null;
        continue;
      }
      pendingSection = entry;
      continue;
    }

    if (!isVisible(entry)) continue;

    if (!pendingSection) {
      result.push(entry);
      continue;
    }

    pendingItems.push(entry);
  }

  flushSection();
  return result;
};
